package top.wcpe.allincore.ui.runtime.ir.api

import top.wcpe.allincore.dsl.ir.model.UiBinding
import top.wcpe.allincore.dsl.ir.model.UiNode
import top.wcpe.allincore.ui.runtime.host.UiBiFunction
import top.wcpe.allincore.ui.runtime.internal.concurrent.ConcurrentMutableMap
import top.wcpe.allincore.ui.runtime.reactive.Signal
import kotlin.jvm.JvmField
import kotlin.jvm.JvmStatic

/** DSL 槽位读取 API。 */
interface SlotApi {
    fun read(id: String): ItemView
    fun register(id: String, groupId: String, key: String) {}
    fun unregister(id: String) {}

    /**
     * 按 groupId / key 直接读取物品视图，无副作用（不注册、不污染动态视图表）。
     * 供全局物品 tooltip 在 host 是 slot、但其元素 id 未被静态收集时按槽位坐标取数。
     * 默认实现返回空视图。
     */
    fun peek(groupId: String, key: String): ItemView = ItemView.EMPTY

    companion object {
        @JvmField val NOOP: SlotApi = object : SlotApi {
            override fun read(id: String): ItemView = ItemView.EMPTY
            override fun register(id: String, groupId: String, key: String) {}
            override fun unregister(id: String) {}
        }

        @JvmStatic
        fun fromIr(root: UiNode?, signalProvider: UiBiFunction<String, String, Signal<ClientItemStack>>): SlotApi {
            if (root == null || signalProvider == null) return NOOP
            val views: MutableMap<String, ItemView> = LinkedHashMap()
            collectSlotViews(root, signalProvider, views)
            return DynamicSlotApi(signalProvider, views)
        }

        private fun collectSlotViews(
            node: UiNode,
            signalProvider: UiBiFunction<String, String, Signal<ClientItemStack>>,
            views: MutableMap<String, ItemView>,
        ) {
            if (node.kind == "slot") {
                val source = readStringLiteral(node, "source")
                var key = readStringLiteral(node, "key")
                if (key == null) {
                    val index = readIntLiteral(node, "index")
                    if (index != null) key = "slot_$index"
                }
                if (source != null && key != null) {
                    views[node.id] = ItemView.of(signalProvider.apply(source, key))
                }
            }
            for (child in node.children) collectSlotViews(child, signalProvider, views)
        }

        private fun readStringLiteral(node: UiNode, name: String): String? {
            val binding = node.props[name] ?: return null
            if (binding.type != UiBinding.BindingType.LITERAL) return null
            return binding.literalValue as? String
        }

        private fun readIntLiteral(node: UiNode, name: String): Int? {
            val binding = node.props[name] ?: return null
            if (binding.type != UiBinding.BindingType.LITERAL) return null
            return (binding.literalValue as? Number)?.toInt()
        }
    }

    /** 同时支持静态收集与运行时动态注册的 SlotApi 实现。 */
    class DynamicSlotApi(
        private val signalProvider: UiBiFunction<String, String, Signal<ClientItemStack>>,
        private val staticViews: MutableMap<String, ItemView>,
    ) : SlotApi {
        private val dynamicViews = ConcurrentMutableMap<String, ItemView>()

        override fun read(id: String): ItemView {
            if (id == null) return ItemView.EMPTY
            return dynamicViews[id] ?: staticViews[id] ?: ItemView.EMPTY
        }

        override fun register(id: String, groupId: String, key: String) {
            if (id.isNullOrEmpty() || groupId.isNullOrEmpty()) return
            dynamicViews[id] = ItemView.of(signalProvider.apply(groupId, key))
        }

        override fun unregister(id: String) {
            if (id == null) return
            dynamicViews.remove(id)
        }

        override fun peek(groupId: String, key: String): ItemView {
            if (groupId.isNullOrEmpty()) return ItemView.EMPTY
            return ItemView.of(signalProvider.apply(groupId, key))
        }
    }
}
